<template>
  <div class="checkin-container">
    <!-- 日期显示 -->
    <div class="date-display">
      <div class="date-text">{{ todayText }}</div>
      <div class="week-text">{{ weekText }}</div>
    </div>
    
    <!-- 打卡状态卡片 -->
    <div class="status-card">
      <div class="status-label">当前任务</div>
      <div class="status-value" v-if="currentStage">
        {{ currentStage.name }}
        <span class="need-image" v-if="currentStage.needImage">📷</span>
      </div>
      <div class="status-value complete" v-else>今日打卡完成 🎉</div>
    </div>
    
    <!-- 实时时间 -->
    <div class="time-display">
      <div class="time-text">{{ currentTime }}</div>
    </div>
    
    <!-- 图片上传区域 (需要图片时显示) -->
    <div class="upload-area" v-if="currentStage && currentStage.needImage && !skipMode">
      <div class="upload-box" @click="selectImage" v-if="!selectedImage">
        <div class="upload-icon">📷</div>
        <div class="upload-text">点击上传照片</div>
      </div>
      <div class="preview-box" v-else>
        <img :src="previewUrl" alt="预览">
        <div class="remove-btn" @click="removeImage">✕</div>
      </div>
      <input type="file" ref="fileInput" accept="image/*" @change="onFileChange" style="display: none">
    </div>
    
    <!-- 操作按钮 -->
    <div class="action-buttons" v-if="currentStage">
      <button 
        class="btn btn-primary" 
        @click="doCheckin"
        :disabled="loading || (currentStage.needImage && !skipMode && !selectedImage)"
      >
        <span v-if="loading">处理中...</span>
        <span v-else>{{ skipMode ? '确认跳过' : '立即打卡' }}</span>
      </button>
      <button 
        class="btn btn-skip" 
        @click="toggleSkip"
        :disabled="loading"
      >
        {{ skipMode ? '取消跳过' : '跳过此次' }}
      </button>
    </div>
    
    <!-- 今日进度 -->
    <div class="progress-section" v-if="allStages.length > 0">
      <div class="progress-title">今日进度 ({{ completedCount }}/{{ allStages.length }})</div>
      <div class="progress-bar">
        <div class="progress-fill" :style="{ width: progressPercent + '%' }"></div>
      </div>
      <div class="stage-list">
        <div
          v-for="stage in allStages"
          :key="stage.key"
          class="stage-item"
          :class="{
            completed: isStageCompleted(stage.key),
            skipped: isStageSkipped(stage.key),
            current: currentStage && currentStage.key === stage.key,
            'can-upload': isStageCompleted(stage.key) && stage.adminOnlyImage
          }"
          @click="handleStageClick(stage)"
        >
          <span class="stage-icon">
            {{ isStageCompleted(stage.key) ? '✓' : (isStageSkipped(stage.key) ? '—' : '○') }}
          </span>
          <span class="stage-name">{{ stage.name }}</span>
          <span class="stage-time" v-if="getStageTime(stage.key)">{{ getStageTime(stage.key) }}</span>
          <span class="upload-icon" v-if="isStageCompleted(stage.key) && stage.adminOnlyImage">📷</span>
        </div>
      </div>
    </div>
    
    <!-- 补传图片弹窗 -->
    <div v-if="showUploadModal" class="modal-overlay" @click="closeUploadModal">
      <div class="upload-modal" @click.stop>
        <h3>补传图片 - {{ uploadStage?.name }}</h3>
        <div class="upload-area-modal">
          <div class="upload-box" @click="selectSupplementImage" v-if="!supplementImage">
            <div class="upload-icon">📷</div>
            <div class="upload-text">点击上传照片</div>
          </div>
          <div class="preview-box" v-else>
            <img :src="supplementPreviewUrl" alt="预览">
            <div class="remove-btn" @click="removeSupplementImage">✕</div>
          </div>
          <input type="file" ref="supplementFileInput" accept="image/*" @change="onSupplementFileChange" style="display: none">
        </div>
        <div class="modal-buttons">
          <button class="btn btn-cancel" @click="closeUploadModal">取消</button>
          <button class="btn btn-primary" @click="uploadSupplementImage" :disabled="!supplementImage || supplementLoading">
            {{ supplementLoading ? '上传中...' : '确认上传' }}
          </button>
        </div>
      </div>
    </div>
    
    <!-- 提示信息 -->
    <div class="message" v-if="message" :class="messageType">{{ message }}</div>
  </div>
</template>

<script>
import axios from 'axios'

const API_BASE = process.env.VUE_APP_API_URL || 'http://localhost:10182/api'

export default {
  name: 'CheckinApp',
  data() {
    return {
      currentTime: '',
      todayText: '',
      weekText: '',
      currentStage: null,
      allStages: [],
      records: {},
      loading: false,
      message: '',
      messageType: '',
      selectedImage: null,
      previewUrl: '',
      skipMode: false,
      // 补传图片相关
      showUploadModal: false,
      uploadStage: null,
      supplementImage: null,
      supplementPreviewUrl: '',
      supplementLoading: false
    }
  },
  computed: {
    completedCount() {
      return Object.keys(this.records).filter(key => 
        this.records[key] && !this.records[key].skip
      ).length
    },
    progressPercent() {
      return (this.completedCount / this.allStages.length) * 100
    }
  },
  mounted() {
    this.updateTime()
    this.loadTodayData()
    setInterval(() => this.updateTime(), 1000)
  },
  methods: {
    updateTime() {
      const now = new Date()
      this.currentTime = now.toLocaleTimeString('zh-CN', { 
        hour: '2-digit', 
        minute: '2-digit', 
        second: '2-digit',
        hour12: false 
      })
      this.todayText = now.toLocaleDateString('zh-CN', { 
        year: 'numeric', 
        month: 'long', 
        day: 'numeric' 
      })
      const weeks = ['周日', '周一', '周二', '周三', '周四', '周五', '周六']
      this.weekText = weeks[now.getDay()]
    },
    async loadTodayData() {
      try {
        const res = await axios.get(`${API_BASE}/today`)
        if (res.data.success) {
          const data = res.data.data
          this.currentStage = data.currentStage
          this.allStages = data.allStages
          this.records = data.records
        }
      } catch (err) {
        this.showMessage('加载数据失败', 'error')
      }
    },
    isStageCompleted(key) {
      return this.records[key] && !this.records[key].skip
    },
    isStageSkipped(key) {
      return this.records[key] && this.records[key].skip
    },
    getStageTime(key) {
      if (this.records[key]) {
        return this.records[key].time
      }
      return null
    },
    selectImage() {
      this.$refs.fileInput.click()
    },
    onFileChange(e) {
      const file = e.target.files[0]
      if (file) {
        this.selectedImage = file
        this.previewUrl = URL.createObjectURL(file)
      }
    },
    removeImage() {
      this.selectedImage = null
      this.previewUrl = ''
      if (this.$refs.fileInput) {
        this.$refs.fileInput.value = ''
      }
    },
    toggleSkip() {
      this.skipMode = !this.skipMode
      if (this.skipMode) {
        this.removeImage()
      }
    },
    async doCheckin() {
      if (!this.currentStage) return
      
      this.loading = true
      
      const formData = new FormData()
      formData.append('stage', this.currentStage.key)
      formData.append('skip', this.skipMode)
      
      if (this.selectedImage && !this.skipMode) {
        formData.append('image', this.selectedImage)
      }
      
      try {
        const res = await axios.post(`${API_BASE}/checkin`, formData, {
          headers: { 'Content-Type': 'multipart/form-data' }
        })
        
        if (res.data.success) {
          this.showMessage(res.data.message, 'success')
          // 使用 Vue.set 确保响应式更新
          this.$set(this.records, this.currentStage.key, res.data.data.record)
          // 再更新当前阶段
          this.currentStage = res.data.data.currentStage
          this.removeImage()
          this.skipMode = false
        } else {
          this.showMessage(res.data.message, 'error')
        }
      } catch (err) {
        console.error('打卡错误:', err)
        console.error('错误响应:', err.response)
        const errorMsg = err.response?.data?.message || err.message || '打卡失败'
        this.showMessage(errorMsg, 'error')
      } finally {
        this.loading = false
      }
    },
    showMessage(text, type) {
      this.message = text
      this.messageType = type
      setTimeout(() => {
        this.message = ''
      }, 3000)
    },
    // 补传图片相关方法
    handleStageClick(stage) {
      // 只有已完成的 adminOnlyImage 阶段才能点击补传图片
      if (this.isStageCompleted(stage.key) && stage.adminOnlyImage) {
        this.openUploadModal(stage)
      }
    },
    openUploadModal(stage) {
      this.uploadStage = stage
      this.showUploadModal = true
      this.supplementImage = null
      this.supplementPreviewUrl = ''
    },
    closeUploadModal() {
      this.showUploadModal = false
      this.uploadStage = null
      this.supplementImage = null
      this.supplementPreviewUrl = ''
    },
    selectSupplementImage() {
      this.$refs.supplementFileInput.click()
    },
    onSupplementFileChange(e) {
      const file = e.target.files[0]
      if (file) {
        this.supplementImage = file
        this.supplementPreviewUrl = URL.createObjectURL(file)
      }
    },
    removeSupplementImage() {
      this.supplementImage = null
      this.supplementPreviewUrl = ''
      if (this.$refs.supplementFileInput) {
        this.$refs.supplementFileInput.value = ''
      }
    },
    async uploadSupplementImage() {
      if (!this.supplementImage || !this.uploadStage) return
      
      this.supplementLoading = true
      
      const formData = new FormData()
      formData.append('image', this.supplementImage)
      formData.append('stage', this.uploadStage.key)
      
      try {
        const today = new Date().toISOString().split('T')[0]
        const res = await axios.post(`${API_BASE}/records/${today}/image`, formData, {
          headers: { 'Content-Type': 'multipart/form-data' }
        })
        
        if (res.data.success) {
          this.showMessage('图片上传成功', 'success')
          this.closeUploadModal()
          // 刷新数据以显示新图片
          this.loadTodayData()
        } else {
          this.showMessage(res.data.message || '上传失败', 'error')
        }
      } catch (err) {
        console.error('上传错误:', err)
        this.showMessage(err.response?.data?.message || '上传失败', 'error')
      } finally {
        this.supplementLoading = false
      }
    }
  }
}
</script>

<style scoped>
.checkin-container {
  background: rgba(255, 255, 255, 0.95);
  border-radius: 20px;
  padding: 24px;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.3);
  backdrop-filter: blur(10px);
}

.date-display {
  text-align: center;
  margin-bottom: 20px;
}

.date-text {
  font-size: 18px;
  color: #333;
  font-weight: 600;
}

.week-text {
  font-size: 14px;
  color: #666;
  margin-top: 4px;
}

.status-card {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  border-radius: 16px;
  padding: 24px;
  text-align: center;
  margin-bottom: 20px;
  color: white;
}

.status-label {
  font-size: 12px;
  opacity: 0.8;
  text-transform: uppercase;
  letter-spacing: 1px;
  margin-bottom: 8px;
}

.status-value {
  font-size: 28px;
  font-weight: bold;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
}

.status-value.complete {
  font-size: 20px;
}

.need-image {
  font-size: 20px;
}

.time-display {
  text-align: center;
  margin-bottom: 24px;
}

.time-text {
  font-size: 48px;
  font-weight: 200;
  color: #333;
  font-family: 'SF Mono', Monaco, monospace;
  letter-spacing: 2px;
}

.upload-area {
  margin-bottom: 20px;
}

.upload-box {
  border: 2px dashed #667eea;
  border-radius: 12px;
  padding: 40px 20px;
  text-align: center;
  cursor: pointer;
  transition: all 0.3s;
  background: rgba(102, 126, 234, 0.05);
}

.upload-box:hover {
  background: rgba(102, 126, 234, 0.1);
  border-color: #764ba2;
}

.upload-icon {
  font-size: 40px;
  margin-bottom: 8px;
}

.upload-text {
  color: #667eea;
  font-size: 14px;
}

.preview-box {
  position: relative;
  border-radius: 12px;
  overflow: hidden;
  background: #f5f5f5;
}

.preview-box img {
  width: 100%;
  height: 200px;
  object-fit: cover;
  display: block;
}

.remove-btn {
  position: absolute;
  top: 8px;
  right: 8px;
  width: 28px;
  height: 28px;
  background: rgba(0, 0, 0, 0.6);
  color: white;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  font-size: 14px;
}

.action-buttons {
  display: flex;
  gap: 12px;
  margin-bottom: 24px;
}

.btn {
  flex: 1;
  padding: 16px 24px;
  border: none;
  border-radius: 12px;
  font-size: 16px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s;
  outline: none;
}

.btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.btn-primary {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
  box-shadow: 0 4px 15px rgba(102, 126, 234, 0.4);
}

.btn-primary:hover:not(:disabled) {
  transform: translateY(-2px);
  box-shadow: 0 6px 20px rgba(102, 126, 234, 0.5);
}

.btn-skip {
  background: #f5f5f5;
  color: #666;
}

.btn-skip:hover:not(:disabled) {
  background: #e8e8e8;
}

.progress-section {
  background: #f8f9fa;
  border-radius: 12px;
  padding: 16px;
}

.progress-title {
  font-size: 14px;
  color: #666;
  margin-bottom: 12px;
  font-weight: 500;
}

.progress-bar {
  height: 6px;
  background: #e0e0e0;
  border-radius: 3px;
  margin-bottom: 16px;
  overflow: hidden;
}

.progress-fill {
  height: 100%;
  background: linear-gradient(90deg, #667eea 0%, #764ba2 100%);
  border-radius: 3px;
  transition: width 0.3s ease;
}

.stage-list {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.stage-item {
  display: flex;
  align-items: center;
  padding: 10px 12px;
  border-radius: 8px;
  background: white;
  font-size: 14px;
  transition: all 0.2s;
}

.stage-item.current {
  background: linear-gradient(135deg, rgba(102, 126, 234, 0.1) 0%, rgba(118, 75, 162, 0.1) 100%);
  border: 1px solid rgba(102, 126, 234, 0.3);
}

.stage-item.completed {
  background: rgba(76, 175, 80, 0.1);
}

.stage-item.skipped {
  background: rgba(158, 158, 158, 0.1);
  opacity: 0.6;
}

.stage-icon {
  width: 24px;
  height: 24px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 12px;
  margin-right: 10px;
  flex-shrink: 0;
}

.stage-item.completed .stage-icon {
  background: #4caf50;
  color: white;
}

.stage-item.skipped .stage-icon {
  background: #9e9e9e;
  color: white;
}

.stage-item.current .stage-icon {
  background: #667eea;
  color: white;
}

.stage-item:not(.completed):not(.skipped):not(.current) .stage-icon {
  border: 2px solid #ddd;
  color: #ccc;
}

.stage-name {
  flex: 1;
  color: #333;
}

.stage-time {
  font-size: 12px;
  color: #667eea;
  font-weight: 500;
  font-family: 'SF Mono', monospace;
}

.stage-item.skipped .stage-time {
  color: #999;
  text-decoration: line-through;
}

.message {
  position: fixed;
  bottom: 20px;
  left: 50%;
  transform: translateX(-50%);
  padding: 12px 24px;
  border-radius: 24px;
  font-size: 14px;
  color: white;
  animation: slideUp 0.3s ease;
  z-index: 1000;
}

.message.success {
  background: #4caf50;
}

.message.error {
  background: #f44336;
}

@keyframes slideUp {
  from {
    opacity: 0;
    transform: translateX(-50%) translateY(20px);
  }
  to {
    opacity: 1;
    transform: translateX(-50%) translateY(0);
  }
}

/* 可补传图片的阶段样式 */
.stage-item.can-upload {
  cursor: pointer;
  border: 1px dashed #667eea;
}

.stage-item.can-upload:hover {
  background: rgba(102, 126, 234, 0.15);
}

.stage-item .upload-icon {
  font-size: 16px;
  margin-left: 8px;
}

/* 补传图片弹窗 */
.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.6);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  padding: 20px;
}

.upload-modal {
  background: white;
  border-radius: 16px;
  padding: 24px;
  width: 100%;
  max-width: 400px;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.3);
}

.upload-modal h3 {
  text-align: center;
  margin-bottom: 20px;
  color: #333;
  font-size: 18px;
}

.upload-area-modal {
  margin-bottom: 20px;
}

.upload-area-modal .upload-box {
  border: 2px dashed #ddd;
  border-radius: 12px;
  padding: 40px 20px;
  text-align: center;
  cursor: pointer;
  transition: all 0.3s;
}

.upload-area-modal .upload-box:hover {
  border-color: #667eea;
  background: rgba(102, 126, 234, 0.05);
}

.upload-area-modal .upload-icon {
  font-size: 48px;
  margin-bottom: 12px;
}

.upload-area-modal .upload-text {
  color: #666;
  font-size: 14px;
}

.upload-area-modal .preview-box {
  position: relative;
  border-radius: 12px;
  overflow: hidden;
  border: 1px solid #ddd;
}

.upload-area-modal .preview-box img {
  width: 100%;
  height: 200px;
  object-fit: cover;
  display: block;
}

.modal-buttons {
  display: flex;
  gap: 12px;
}

.modal-buttons .btn {
  flex: 1;
  padding: 12px;
  font-size: 14px;
}

.btn-cancel {
  background: #f5f5f5;
  color: #666;
}

.btn-cancel:hover:not(:disabled) {
  background: #e8e8e8;
}

@media (max-width: 360px) {
  .checkin-container {
    padding: 16px;
  }
  
  .time-text {
    font-size: 36px;
  }
  
  .status-value {
    font-size: 24px;
  }
  
  .btn {
    padding: 14px 20px;
    font-size: 14px;
  }
}
</style>